/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { DrawInfo } from "../models/DrawInfo";
import { Draw } from "../models/Draw";
import { Ticket } from "../models/Ticket";
import { TicketMapper } from "../models/TicketMapper";
import {
  CHAIN,
  DRAWS_ANIMATION_TIME_COMPENSATION,
  DRAWS_IN_HOME,
  DRAWS_IN_PAGE,
  getIndexerQuery,
  isMiniApp,
  LAST_PREVIOUS_DRAW_PROMPTED_KEY,
  SUBGRAPH_ENDPOINT,
} from "../utils/Constants";
import {
  bitMillionsAbi,
  bitMillionsAddress,
  usdcAbi,
  usdcAddress,
  useWatchBitMillionsDrawClosedEvent,
  useWatchBitMillionsDrawOpenedEvent,
  useWatchBitMillionsDrawPostponedEvent,
  useWatchBitMillionsDrawUpdatedEvent,
  useWatchBitMillionsLastUpkeepUpdatedEvent,
  useWatchBitMillionsLotteryStateChangedEvent,
  useWatchBitMillionsPlayerReferredEvent,
  useWatchBitMillionsUpkeepConfigSetEvent,
} from "../generated";
import {
  BaseError,
  useConfig,
  useConnect,
  useConnection,
  useConnectorClient,
} from "wagmi";
import { Address, Log, parseEventLogs, zeroAddress } from "viem";
import {
  xDrawClosedEvent,
  xDrawOpenedEvent,
  xDrawPostponedEvent,
  xLotteryInfo,
  xLotteryState,
  xLotteryStateChangedEvent,
  xUpkeepConfig,
  xUpkeepConfigSetEvent,
  xUserInfo as xUserInfo,
  xDrawUpdatedEvent,
  xUpkeepPerformedEvent as xLastUpkeepUpdatedEvent,
  IndexerDraw,
  StatisticsQuery,
  IndexerMainNumberStatistics,
  IndexerLuckyStarStatistics,
  IndexerPlayer,
  IndexerLotteryStatistics,
  IndexerCharity,
  xPlayerReferredEvent,
} from "../web3/Types";
import { useDialog } from "../hooks/useDialog";
import { PlayerDataContext } from "./PlayerDataContext";
import DrawPostponedDialog from "../components/dialogs/DrawPostponedDialog";
import {
  dismissSnackbar,
  handleError,
  handleEventError,
  handleTransactionStatus,
  openTransactionUrl,
  parseDate,
  snackBar,
} from "../utils/Utils";
import { readContract, sendCalls, switchChain } from "@wagmi/core";
import { UserInfo } from "../models/UserInfo";
import { SentryUtils } from "../utils/SentryUtils";
import { appKit } from "../utils/Constants";
import { PlayerStatistics } from "../models/PlayerStatistics";
import DrawDialog from "../components/dialogs/DrawDialog";
import { USDC } from "../models/UsdcValue";
import { PrizeTierResults } from "../models/PrizeTierResults";
import { Set } from "../models/Set";
import { PrizeFund } from "../models/LotteryConfig";
import { farcasterMiniApp as miniAppConnector } from "@farcaster/miniapp-wagmi-connector";
import { LotteryStatistics } from "../models/LotteryStatistics";
import { Frequency } from "../models/Frequency";
import { Charity } from "../models/Charity";
import { HistoryFilter } from "../models/HistoryFilter";
import { simulateCalls } from "viem/actions";

export const PlayerDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Dialog
  const { openDialog, closeDialog } = useDialog();

  // Wagmi Hooks
  const { address, isConnecting, isConnected } = useConnection();
  const connect = useConnect();

  const config = useConfig();
  const { data: walletClient } = useConnectorClient();

  // State
  const [buying, setBuying] = useState(false);
  const [withdrawingPrizes, setWithdrawingPrizes] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [updatingDraws, setUpdatingDraws] = useState(false);
  const [drawInfo, setDrawInfo] = useState<DrawInfo>();
  const [drawDate, setDrawDate] = useState<Date>();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [statistics, setStatistics] = useState<LotteryStatistics>(
    LotteryStatistics.empty(),
  );
  const [playerStatistics, setPlayerStatistics] = useState<PlayerStatistics>(
    PlayerStatistics.empty(),
  );
  const [drawsInHistory, setDrawsInHistory] = useState<Draw[]>([]);
  const [drawsInHome, setDrawsInHome] = useState<Draw[]>([]);
  const [upkeepConfig, setUpkeepConfig] = useState<xUpkeepConfig>();
  const [lastUpkeep, setLastUpkeep] = useState<bigint>();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [ticketMappers, setTicketMappers] = useState<TicketMapper[]>([]);
  const [isPostponed, setPostponed] = useState(false);
  const [charities, setCharities] = useState<Charity[]>([]);
  const [withdrawingDonations, setWithdrawingDonations] = useState(false);
  const [withdrawingRoyalties, setWithdrawingRoyalties] = useState(false);

  const wasHidden = useRef(false);

  // Keyboard ESC dialog close
  useEffect(() => {
    if (typeof window == "undefined") return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key == "Escape") closeDialog();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [closeDialog]);

  // Visibility and pagehide tracking (robust for embedded/mobile browsers)
  useEffect(() => {
    if (typeof document == "undefined" && typeof window == "undefined") return;
    const handleVisibilityChange = () => {
      if (
        typeof document != "undefined" &&
        document.visibilityState == "hidden"
      ) {
        wasHidden.current = true;
      }
    };
    const handlePageHide = () => {
      wasHidden.current = true;
    };
    if (typeof document != "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window != "undefined") {
      window.addEventListener("pagehide", handlePageHide);
    }
    return () => {
      if (typeof document != "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
      if (typeof window != "undefined") {
        window.removeEventListener("pagehide", handlePageHide);
      }
    };
  }, []);

  // Connect wallet
  const connectWallet = useCallback(async () => {
    if (!isMiniApp) {
      await appKit.open();
    } else {
      await connect.mutate({ connector: miniAppConnector() });
    }

    await switchChain(config, { chainId: CHAIN.id });
  }, []);

  // Show previous draw dialog
  const showPreviousDraw = useCallback(
    (draw: Draw, force: boolean = false) => {
      if (window.location.pathname != "/") return;

      const lastPreviousDrawUpdated: string | null = localStorage.getItem(
        LAST_PREVIOUS_DRAW_PROMPTED_KEY,
      );

      if (
        force ||
        lastPreviousDrawUpdated == null ||
        BigInt(lastPreviousDrawUpdated) < draw.number
      ) {
        openDialog(
          <DrawDialog
            draw={draw}
            address={address}
            onClose={closeDialog}
            announce={true}
          />,
        );

        localStorage.setItem(
          LAST_PREVIOUS_DRAW_PROMPTED_KEY,
          draw.number.toString(),
        );
      }
    },
    [address, openDialog, closeDialog],
  );

  /**
   * @description Convert a tuple formatted LotteryInfo to a known typed __{@link xLotteryInfo}__.
   * @param rawLotteryInfo The tuple formatted LotteryInfo to curate.
   * @returns A curated __{@link xLotteryInfo}__ object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function curateLotteryInfo(rawLotteryInfo: any): xLotteryInfo {
    return {
      nextLotteryConfig: rawLotteryInfo[0],
      upkeepConfig: rawLotteryInfo[1],
      vrfConfig: rawLotteryInfo[2],
      lotteryState: rawLotteryInfo[3],
      lastUpkeep: rawLotteryInfo[4],
      drawNumber: rawLotteryInfo[5],
      lotteryConfig: rawLotteryInfo[6],
      jackpot: rawLotteryInfo[7],
      charityAddress: rawLotteryInfo[8],
      proceeds: rawLotteryInfo[9],
    };
  }

  /**
   * @description Convert a tuple formatted UserInfo to a known typed __{@link xUserInfo}__.
   * @param rawUserInfo The tuple formatted UserInfo to curate.
   * @returns A curated __{@link xUserInfo}__ object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function curateUserInfo(rawUserInfo: any): xUserInfo {
    return {
      player: rawUserInfo[0],
      lastDrawPrize: rawUserInfo[1],
      currentTickets: rawUserInfo[2],
      charity: rawUserInfo[3],
      isOwner: rawUserInfo[4],
    };
  }

  useEffect(() => {
    if (wasHidden.current) {
      wasHidden.current = false;
    } else {
      // readContracts(config, {
      //   contracts: [
      //     {
      //       ...bitMillionsContract,
      //       functionName: "getNextDrawConfig",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "getUpkeepConfig",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "getVRFConfig",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "s_lotteryState",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "s_lastUpkeep",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "s_drawNumber",
      //     },
      //     {
      //       ...bitMillionsContract,
      //       functionName: "getCurrentDraw",
      //     },
      //     // {
      //     //   ...multicall3Contract,
      //     //   functionName: "getCurrentBlockTimestamp",
      //     // },
      //   ],
      // }).then

      //* if a second multicall is needed, call the charity struct and the current and previous draw (previous draw because the subgraph might not be up to date yet)

      readContract(config, {
        abi: bitMillionsAbi,
        address: bitMillionsAddress[CHAIN.id],
        functionName: "getLottery",
        account: address,
      }).then((rawLotteryInfo) => {
        const lotteryInfo: xLotteryInfo = curateLotteryInfo(rawLotteryInfo);
        const drawInfo = DrawInfo.fromLotteryInfo(lotteryInfo);
        const drawDate = parseDate(
          lotteryInfo.upkeepConfig as xUpkeepConfig,
          Number(lotteryInfo.lastUpkeep),
        );
        setUpkeepConfig(lotteryInfo.upkeepConfig as xUpkeepConfig);
        setLastUpkeep(lotteryInfo.lastUpkeep);
        setDrawInfo(drawInfo);
        setDrawDate(drawDate);
        setIsOpen(lotteryInfo.lotteryState == xLotteryState.Open);
        // Handle the first Indexer query:

        console.log("Fetching subgraph...");
        fetch(SUBGRAPH_ENDPOINT, {
          method: "POST",
          body: JSON.stringify({
            query: getIndexerQuery({
              playerAddress: address,
              page: 0,
              count: DRAWS_IN_PAGE,
              noDraws: false,
              noPlayer: address == undefined,
              noStatistics: false,
              noCharities: false,
              playedFilter: false,
              benefitedFilter: false,
            }),
          }),
        })
          .then((response) => response.json())
          .then((result) => {
            if (result.data) {
              const data: StatisticsQuery = result.data;
              // Handle draw data:
              const drawsData: IndexerDraw[] | undefined = data.draws;
              if (drawsData) {
                // Map each draw object to a Draw model instance
                const draws = drawsData.map((draw: IndexerDraw) =>
                  Draw.fromGraph(draw),
                );
                setDrawsInHome(draws.slice(0, DRAWS_IN_HOME));
                setDrawsInHistory(draws);
                if (draws.length > 0) showPreviousDraw(draws[0], false);
              }
              new Promise((f) =>
                setTimeout(f, DRAWS_ANIMATION_TIME_COMPENSATION),
              ).then(() => {
                setUpdatingDraws(false);
              });
              // Handle player statistics:
              const playerData: IndexerPlayer | undefined = data.player;
              if (address != undefined && playerData) {
                if (playerData) {
                  setPlayerStatistics(PlayerStatistics.fromGraph(playerData));
                }
              }
              // Handle statistics:
              const numberData: IndexerMainNumberStatistics[] | undefined =
                data.mainNumberStatistics_collection;
              const starData: IndexerLuckyStarStatistics[] | undefined =
                data.luckyStarStatistics_collection;
              const lotteryData: IndexerLotteryStatistics | undefined =
                data.lotteries[0];
              if (numberData && starData && lotteryData) {
                const statistics = LotteryStatistics.fromGraph(
                  numberData.map((stat) => Frequency.fromGraph(stat)),
                  starData.map((stat) => Frequency.fromGraph(stat)),
                  lotteryData,
                  lotteryInfo.lotteryConfig,
                );
                setStatistics(statistics);
              }
              // Handle charities:
              const charitiesData: IndexerCharity[] | undefined =
                data.charities;
              if (charitiesData) {
                const charities = charitiesData.map((charity) =>
                  Charity.fromGraph(charity),
                );
                setCharities(charities);
              }
              // Check if current draw number is > than the latest indexer draw + 1:
              const latestDrawNumber = drawsInHome[0]?.number;
              if (
                latestDrawNumber != undefined &&
                drawInfo.drawNumber > latestDrawNumber + 1n
              ) {
                snackBar(
                  "We're still processing the latest draw, please wait a moment and refresh the page.",
                  "🔄",
                  Infinity,
                );
              }
            }
          })
          .catch((err) => {
            SentryUtils.customIssue("Failed to get lottery info", "error");
            console.log("Error getting lottery info: ", err);
          });
      });
    }
  }, []);

  /**
   * @description Fetch Player Info.
   * @note Triggers when the connection to the wallet changes.
   */
  //todo change to readContracts
  useEffect(() => {
    if (wasHidden.current) {
      wasHidden.current = false;
    } else {
      if (address != undefined) {
        // readContracts(config, {
        //   contracts: [
        //     {
        //       ...bitMillionsContract,
        //       functionName: "getPlayer",
        //       args: [address],
        //     },
        //     {
        //       ...bitMillionsContract,
        //       functionName: "s_charities",
        //       args: [address],
        //     },
        //   ],
        // });

        // Sync user info from contract:
        setUpdatingDraws(true);
        readContract(config, {
          abi: bitMillionsAbi,
          address: bitMillionsAddress[CHAIN.id],
          functionName: "getUser",
          account: address,
          args: [address],
        })
          .then((rawUserInfo) => {
            setUserInfo(UserInfo.fromUserInfo(curateUserInfo(rawUserInfo)));

            console.log("Fetching subgraph...");
            fetch(SUBGRAPH_ENDPOINT, {
              method: "POST",
              body: JSON.stringify({
                query: getIndexerQuery({
                  playerAddress: address,
                  page: 0,
                  count: DRAWS_IN_PAGE,
                  noDraws: false,
                  noPlayer: false,
                  noStatistics: true,
                  noCharities: true,
                  playedFilter: false,
                  benefitedFilter: false,
                }),
              }),
            })
              .then((response) => response.json())
              .then((result) => {
                if (result.data) {
                  const data: StatisticsQuery = result.data;
                  // Handle player statistics:
                  const playerData = data.player;
                  if (playerData) {
                    setPlayerStatistics(PlayerStatistics.fromGraph(playerData));
                  }
                  const drawsData: IndexerDraw[] | undefined = data.draws;
                  // Handle draw data:
                  if (drawsData) {
                    // Map each draw object to a Draw model instance
                    const draws = drawsData.map((draw: IndexerDraw) =>
                      Draw.fromGraph(draw),
                    );
                    setDrawsInHome(draws.slice(0, DRAWS_IN_HOME));
                    setDrawsInHistory(draws);
                    if (draws.length > 0) showPreviousDraw(draws[0], false);
                  }
                }
              })
              .catch((err) => {
                console.log("Error fetching data: ", err);
              });
          })
          .catch((err) => {
            SentryUtils.customIssue("Failed to sync user info", "error");
            console.error("Failed to sync user info", err);
          });
      } else {
        setUserInfo(undefined);
      }
    }
  }, [address]);

  /**
   * @description Initialize ticket mappers.
   * @note Ignores re-render caused by browser tab switching.
   * @note Resets ticket mappers when the draw info changes
   * (i.e. when a draw closes and another starts)
   */
  useEffect(() => {
    if (drawInfo == undefined) return;
    if (wasHidden.current) {
      wasHidden.current = false;
    } else {
      setTicketMappers((prev) => {
        let limit = Number(drawInfo.drawConfig.ticketLimitPerPurchase);
        if (!Number.isFinite(limit) || limit < 0) limit = 0;
        const numberPool = Number(drawInfo.drawConfig.numberPool);
        const starPool = Number(drawInfo.drawConfig.starPool);
        // If empty or wrong length, re-create all
        if (prev.length !== limit) {
          return Array.from({ length: limit }, (_, i) =>
            TicketMapper.empty(i, numberPool, starPool),
          );
        }
        // Otherwise, check if any need to be reset
        return prev.map((tm, i) => {
          if (
            tm.numbers.length !== numberPool ||
            tm.stars.length !== starPool ||
            tm.numbers.filter(Boolean).length >
              Number(drawInfo.drawConfig.numbersPerTicket) ||
            tm.stars.filter(Boolean).length >
              Number(drawInfo.drawConfig.starsPerTicket)
          ) {
            return TicketMapper.empty(i, numberPool, starPool);
          }
          return tm;
        });
      });
    }
  }, [drawInfo]);

  /**
   * @description Fetches draws from the subgraph with pagination support.
   * @param page The page number to fetch.
   * @param filter The history filter to apply.
   */
  const fetchDrawsInPage = useCallback(
    async (page: number, filter: HistoryFilter) => {
      if (drawInfo == undefined) return;
      if (drawInfo.drawNumber <= 1) return;

      setUpdatingDraws(true);
      await new Promise((f) =>
        setTimeout(f, DRAWS_ANIMATION_TIME_COMPENSATION),
      );

      const latestDrawId: bigint = BigInt(
        Number(drawInfo.drawNumber - 1n) - page * DRAWS_IN_PAGE,
      );
      const count = Math.min(DRAWS_IN_PAGE, Number(latestDrawId));

      console.log("Fetching subgraph...");
      fetch(SUBGRAPH_ENDPOINT, {
        method: "POST",
        body: JSON.stringify({
          query: getIndexerQuery({
            playerAddress: address,
            page: page,
            count: count,
            noPlayer: true,
            noDraws: false,
            noStatistics: true,
            noCharities: true,
            playedFilter: filter == HistoryFilter.PLAYED,
            benefitedFilter: filter == HistoryFilter.BENEFITED,
          }),
        }),
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.data) {
            const drawsData = result.data.draws;
            if (drawsData) {
              const draws = drawsData.map((draw: IndexerDraw) =>
                Draw.fromGraph(draw),
              );
              setDrawsInHistory(draws);
            }
            new Promise((f) =>
              setTimeout(f, DRAWS_ANIMATION_TIME_COMPENSATION),
            ).then(() => {
              setUpdatingDraws(false);
            });
          }
        })
        .catch((err) => {
          console.error("Error fetching data: ", err);
          setUpdatingDraws(false);
        });
    },
    [address, drawInfo],
  );

  /**
   * @description Handle splash screen logic. Also responsible for the
   * transition animation timing.
   * @note Shows the splash screen while the required variables are not
   * yet loaded.
   */
  useEffect(() => {
    if (wasHidden.current) {
      wasHidden.current = false;
    } else {
      // Check if all the synchronization fetches are finished
      if (drawInfo && drawDate && !!address == !!userInfo) {
        setAnimating(true);
        const splashTimer = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return () => {
          clearTimeout(splashTimer);
        };
      }
    }
  }, [drawDate, drawInfo, address, userInfo]);

  /**
   * LISTENERS
   */
  // --- Memoized event handlers to avoid re-registering and spamming newFilter calls ---
  // Each handler is memoized and stable, so the event hooks do not re-initialize
  const handleUpkeepConfigSet = useCallback(
    (logs: Log[]) => {
      console.log("UpkeepConfigSet Event:", logs);
      const parsedLogs = parseEventLogs({
        abi: bitMillionsAbi,
        logs,
        strict: true,
      });
      for (const log of parsedLogs) {
        try {
          const event: xUpkeepConfigSetEvent =
            log.args as xUpkeepConfigSetEvent;
          const newDrawDate = parseDate(event.upkeepConfig, Number(lastUpkeep));
          setUpkeepConfig(event.upkeepConfig as xUpkeepConfig);
          setDrawDate(newDrawDate);
          if (isPostponed) {
            setPostponed(false);
            openDialog(
              <DrawPostponedDialog
                onClose={closeDialog}
                newDate={newDrawDate}
              />,
            );
          }
        } catch (e) {
          handleEventError("UpkeepConfigSet", e);
        }
      }
    },
    [lastUpkeep, isPostponed, openDialog, closeDialog],
  );

  const handleLastUpkeepUpdated = useCallback((logs: Log[]) => {
    console.log("LastUpkeepUpdated Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xLastUpkeepUpdatedEvent =
          log.args as xLastUpkeepUpdatedEvent;
        setLastUpkeep(event.lastUpkeep);
        const newDrawDate = parseDate(upkeepConfig!, Number(event.lastUpkeep));
        setDrawDate(newDrawDate);
      } catch (e) {
        handleEventError("LastUpkeepUpdated", e);
      }
    }
  }, []);

  const handleLotteryStateChanged = useCallback((logs: Log[]) => {
    console.log("LotteryStateChanged Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xLotteryStateChangedEvent =
          log.args as xLotteryStateChangedEvent;
        setIsOpen(event.toState == xLotteryState.Open);
      } catch (e) {
        handleEventError("LotteryStateChanged", e);
      }
    }
  }, []);

  const handleDrawUpdated = useCallback((logs: Log[]) => {
    console.log("DrawUpdated Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xDrawUpdatedEvent = log.args as xDrawUpdatedEvent;
        setDrawInfo((drawInfo) => drawInfo?.updateWithDrawUpdatedEvent(event));
      } catch (e) {
        handleEventError("DrawUpdated", e);
      }
    }
  }, []);

  const handleDrawOpened = useCallback((logs: Log[]) => {
    console.log("DrawOpened Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xDrawOpenedEvent = log.args as xDrawOpenedEvent;
        setDrawInfo(DrawInfo.fromDrawOpenedEvent(event));
      } catch (e) {
        handleEventError("DrawOpened", e);
      }
    }
  }, []);

  const handleDrawClosed = useCallback(
    (logs: Log[]) => {
      console.log("DrawClosed Event:", logs);
      const parsedLogs = parseEventLogs({
        abi: bitMillionsAbi,
        logs,
        strict: true,
      });
      for (const log of parsedLogs) {
        try {
          const event: xDrawClosedEvent = log.args as xDrawClosedEvent;
          if (drawInfo == undefined) {
            SentryUtils.customIssue(
              "DrawClosed event received before current draw information gotten.",
              "warning",
            );
            return;
          }
          let rollover = true;
          let totalWinners = 0n;
          // Calculate new draw stuff:
          const results: PrizeTierResults[] = event.tierWinners.map((tier) => {
            const combination: Set = new Set(
              Number(tier.tier.mainNumbers),
              Number(tier.tier.luckyStars),
            );

            if (
              tier.tier.mainNumbers == drawInfo!.drawConfig.numbersPerTicket &&
              tier.tier.luckyStars == drawInfo!.drawConfig.starsPerTicket
            ) {
              rollover = tier.winners == 0n;
              return new PrizeTierResults(
                event.tierWinners.indexOf(tier),
                new Set(
                  Number(tier.tier.mainNumbers),
                  Number(tier.tier.luckyStars),
                ),
                new USDC(
                  drawInfo.jackpot.value /
                    (tier.winners == 0n ? 1n : tier.winners),
                ),
                drawInfo.jackpot,
                tier.winners,
              );
            }

            const prizeFund: PrizeFund | undefined =
              drawInfo!.drawConfig.prizeFundDistribution.find(
                (pfd) =>
                  pfd.numberAmount == combination.mainNumbers &&
                  pfd.starAmount == combination.luckyStars,
              );

            if (prizeFund == undefined) {
              throw new Error(
                `Prize fund distribution not found for tier: ${combination.toString()}`,
              );
            }
            const prizeFundAmount: bigint = BigInt(
              Math.floor(
                Number(drawInfo.prizePool.value) * prizeFund.proportion,
              ),
            );
            totalWinners += tier.winners;
            return new PrizeTierResults(
              event.tierWinners.indexOf(tier),
              new Set(
                Number(tier.tier.mainNumbers),
                Number(tier.tier.luckyStars),
              ),
              tier.winners != 0n
                ? new USDC(prizeFundAmount / tier.winners)
                : new USDC(0n),
              new USDC(prizeFundAmount),
              tier.winners,
            );
          });
          // New draw:
          const closedDraw = new Draw(
            drawInfo.drawNumber,
            Ticket.fromTicket(event.ticket),
            drawDate!,
            drawInfo!.jackpot,
            -1, // entries
            charities.find(
              (charity) => charity.address == drawInfo.charityAddress,
            ),
            drawInfo.raised,
            results,
            rollover,
            drawInfo!.drawConfig,
            Number(totalWinners),
            -1, // win ratio
            drawInfo.prizePool,
            userInfo?.ticketsBought ?? [],
          );
          if (window.location.pathname == "/") {
            setDrawsInHome((draws) =>
              [closedDraw, ...draws].slice(0, DRAWS_IN_PAGE),
            );
          }
          // Update toClaim information
          setUserInfo((userInfo) => {
            const newUserInfo = userInfo?.clearTicketsBought();
            newUserInfo?.updateToClaim(
              (userInfo?.toClaim?.value ?? 0n) +
                (closedDraw.userPrize?.value ?? 0n),
              (userInfo?.donationsToClaim?.value ?? 0n) +
                (closedDraw.charity?.address == address
                  ? (closedDraw.raised?.value ?? 0n)
                  : 0n),
              undefined,
            );
            return newUserInfo;
          });
          showPreviousDraw(closedDraw, true);
        } catch (e) {
          handleEventError("DrawClosed", e);
        }
      }
    },
    [drawInfo, drawDate, showPreviousDraw],
  );

  const handleDrawPostponed = useCallback((logs: Log[]) => {
    console.log("DrawPostponed Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xDrawPostponedEvent = log.args as xDrawPostponedEvent;
        if (event.drawNumber != undefined) {
          setPostponed(true);
        }
      } catch (e) {
        handleEventError("DrawPostponed", e);
      }
    }
  }, []);

  const handlePlayerReferred = useCallback((logs: Log[]) => {
    console.log("PlayerReferred Event:", logs);
    const parsedLogs = parseEventLogs({
      abi: bitMillionsAbi,
      logs,
      strict: true,
    });
    for (const log of parsedLogs) {
      try {
        const event: xPlayerReferredEvent = log.args as xPlayerReferredEvent;
        if (event.referrerAddress == address) {
          setUserInfo((userInfo) =>
            userInfo ? userInfo.addReferralPoints(event.points) : undefined,
          );
        }
      } catch (e) {
        handleEventError("PlayerReferred", e);
      }
    }
  }, []);

  // --- Register event listeners with stable handlers ---
  useWatchBitMillionsUpkeepConfigSetEvent({ onLogs: handleUpkeepConfigSet });
  useWatchBitMillionsLastUpkeepUpdatedEvent({
    onLogs: handleLastUpkeepUpdated,
  });
  useWatchBitMillionsLotteryStateChangedEvent({
    onLogs: handleLotteryStateChanged,
  });
  useWatchBitMillionsDrawUpdatedEvent({ onLogs: handleDrawUpdated });
  useWatchBitMillionsDrawOpenedEvent({ onLogs: handleDrawOpened });
  useWatchBitMillionsDrawClosedEvent({ onLogs: handleDrawClosed });
  useWatchBitMillionsDrawPostponedEvent({ onLogs: handleDrawPostponed });
  useWatchBitMillionsPlayerReferredEvent({ onLogs: handlePlayerReferred });

  /**
   * @description Buy tickets.
   * @note Triggers when the user clicks the buy button, after filling tickets.
   * @param ticketMappers The tickets to buy.
   */
  async function buyTickets(
    ticketMappers: TicketMapper[],
    referrer: Address | undefined,
    useCoupon: boolean,
  ): Promise<void> {
    if (address == undefined || drawInfo == undefined) return;
    setBuying(true);
    try {
      snackBar("Check your wallet to accept the transaction.", "ℹ️", undefined);

      try {
        // Generate tickets
        const tickets: Ticket[] = [];
        for (const ticketMapper of ticketMappers) {
          const ticket: Ticket | null = ticketMapper.toTicket(
            Number(drawInfo.drawConfig.numbersPerTicket),
            Number(drawInfo.drawConfig.starsPerTicket),
          );
          if (ticket != null) {
            tickets.push(ticket);
          }
        }

        // Calculate value
        const value = BigInt(
          tickets.length *
            Number(drawInfo.drawConfig.ticketPrice) *
            (useCoupon
              ? (Number(drawInfo.drawConfig.ticketLimitPerPurchase) - 1) /
                Number(drawInfo.drawConfig.ticketLimitPerPurchase)
              : 1),
        );

        // const simulateApprove = await simulateContract(config, {
        //   account: address,
        //   abi: usdcAbi,
        //   chainId: CHAIN.id,
        //   address: usdcAddress[CHAIN.id],
        //   functionName: "approve",
        //   args: [bitMillionsAddress[CHAIN.id], value],
        // });

        // console.log("Simulate approve result:", simulateApprove);

        // const hashApprove = await writeContract(config, {
        //   account: address,
        //   abi: usdcAbi,
        //   chainId: CHAIN.id,
        //   address: usdcAddress[CHAIN.id],
        //   functionName: "approve",
        //   args: [bitMillionsAddress[CHAIN.id], value],
        // });

        // await handleTransactionHash(
        //   walletClient!,
        //   hashApprove,
        //   async (hash: Address) => {
        //     console.log("Approve transaction hash:", hash);
        //   },
        //   async (failedHash: Address) => {
        //     handleError(
        //       `The transaction ${failedHash} failed.`,
        //       "Approve HandleTransactionStatus",
        //       undefined,
        //       <div>
        //         <p>We are very sorry, the transaction failed.</p>
        //         <p
        //           className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
        //           onClick={() => {
        //             openTransactionUrl(failedHash);
        //           }}
        //         >
        //           {failedHash.toString()}
        //         </p>
        //       </div>,
        //     );
        //   },
        // );

        // const simulateBuyTickets = await simulateContract(config, {
        //   account: address,
        //   abi: bitMillionsAbi,
        //   chainId: CHAIN.id,
        //   address: bitMillionsAddress[CHAIN.id],
        //   functionName: "buyTickets",
        //   args: [
        //     drawInfo.drawNumber,
        //     tickets.map((ticket) => ({
        //       mainNumbers: ticket.mainNumbers,
        //       luckyStars: ticket.luckyStars,
        //     })),
        //     referrer ?? zeroAddress,
        //     useCoupon,
        //   ],
        // });

        // console.log("Simulate buy tickets result:", simulateBuyTickets);

        // const hashBuyTickets = await writeContract(config, {
        //   account: address,
        //   abi: bitMillionsAbi,
        //   chainId: CHAIN.id,
        //   address: bitMillionsAddress[CHAIN.id],
        //   functionName: "buyTickets",
        //   args: [
        //     drawInfo.drawNumber,
        //     tickets.map((ticket) => ({
        //       mainNumbers: ticket.mainNumbers,
        //       luckyStars: ticket.luckyStars,
        //     })),
        //     referrer ?? zeroAddress,
        //     useCoupon,
        //   ],
        // });

        // await handleTransactionHash(
        //   walletClient!,
        //   hashBuyTickets,
        //   async (hash: Address) => {
        //     SentryUtils.customIssue("Tickets purchased.", "info");
        //     dismissSnackbar();
        //     snackBar(
        //       <div>
        //         <p>Transaction complete! Check your dashboard.</p>
        //         <p
        //           className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
        //           onClick={() => {
        //             openTransactionUrl(hash);
        //           }}
        //         >
        //           {hash.toString()}
        //         </p>
        //       </div>,
        //       "✅",
        //     );
        //     setUserInfo((userInfo) =>
        //       userInfo
        //         ? userInfo.addTicketsBought(tickets.reverse())
        //         : undefined,
        //     );
        //   },
        //   async (failedHash: Address) => {
        //     handleError(
        //       `The transaction ${failedHash} failed.`,
        //       "BuyTickets HandleTransactionStatus",
        //       undefined,
        //       <div>
        //         <p>We are very sorry, the transaction failed.</p>
        //         <p
        //           className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
        //           onClick={() => {
        //             openTransactionUrl(failedHash);
        //           }}
        //         >
        //           {failedHash.toString()}
        //         </p>
        //       </div>,
        //     );
        //   },
        // );

        await switchChain(config, { chainId: CHAIN.id });

        const { results } = await simulateCalls(config.getClient(), {
          account: address,
          calls: [
            {
              abi: usdcAbi,
              to: usdcAddress[CHAIN.id],
              functionName: "approve",
              args: [bitMillionsAddress[CHAIN.id], value],
            },
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "buyTickets",
              args: [
                drawInfo.drawNumber,
                tickets.map((ticket) => ({
                  mainNumbers: ticket.mainNumbers,
                  luckyStars: ticket.luckyStars,
                })),
                referrer ?? zeroAddress,
                useCoupon,
              ],
            },
          ],
        });

        if (results[0].error) throw results[0].error;

        const { id } = await sendCalls(config, {
          account: address,
          chainId: CHAIN.id,
          calls: [
            {
              abi: usdcAbi,
              to: usdcAddress[CHAIN.id],
              functionName: "approve",
              args: [bitMillionsAddress[CHAIN.id], value],
            },
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "buyTickets",
              args: [
                drawInfo.drawNumber,
                tickets.map((ticket) => ({
                  mainNumbers: ticket.mainNumbers,
                  luckyStars: ticket.luckyStars,
                })),
                referrer ?? zeroAddress,
                useCoupon,
              ],
            },
          ],
        });

        // snackBar("You will receive your tickets soon...", "⏳");

        await handleTransactionStatus(
          walletClient!,
          id,
          async (hash: Address) => {
            SentryUtils.customIssue("Tickets purchased.", "info");
            dismissSnackbar();
            snackBar(
              <div>
                <p>Transaction complete! Check your dashboard.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(hash);
                  }}
                >
                  {hash.toString()}
                </p>
              </div>,
              "✅",
            );
            setUserInfo((userInfo) =>
              userInfo
                ? userInfo.addTicketsBought(tickets.reverse())
                : undefined,
            );
          },
          async (failedHash: Address) => {
            handleError(
              `The transaction ${failedHash} failed.`,
              "BuyTickets HandleTransactionStatus",
              undefined,
              <div>
                <p>We are very sorry, the transaction failed.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(failedHash);
                  }}
                >
                  {failedHash.toString()}
                </p>
              </div>,
            );
          },
        );

        // Reset ticket mappers
        setTicketMappers(
          Array.from(
            {
              length: Number(drawInfo.drawConfig.ticketLimitPerPurchase),
            },
            (_, index) =>
              TicketMapper.empty(
                index,
                Number(drawInfo.drawConfig.numberPool),
                Number(drawInfo.drawConfig.starPool),
              ),
          ),
        );
      } catch (error) {
        // let errorMessage = "Something went wrong.";
        // let errorLog = "";

        // if (error instanceof BaseError) {
        //   const revertError = error.walk(
        //     (error) => error instanceof ContractFunctionRevertedError,
        //   );
        //   if (revertError instanceof ContractFunctionRevertedError) {
        //     const errorName = revertError.data!.errorName;
        //     switch (errorName) {
        //       case "WrongLotteryState":
        //         errorMessage =
        //           "The lottery is not open. Please wait for the next draw.";
        //         break;
        //       case "CannotApplyDiscountAndRefer":
        //         errorMessage =
        //           "You can either apply a discount or refer a friend, not both.";
        //         break;
        //       case "CannotReferYourself":
        //         errorMessage = "You cannot refer yourself.";
        //         break;
        //       case "NotEnoughPoints":
        //         errorMessage =
        //           "You do not have enough points to buy tickets with a coupon.";
        //         break;
        //       case "TicketTotalLimitExceeded":
        //         errorMessage =
        //           "You have reached the maximum number of tickets you can buy.";
        //         break;
        //       case "TicketBuyLimitExceeded":
        //         errorMessage =
        //           "You have reached the maximum number of tickets you can buy in a single transaction.";
        //         break;
        //       case "IncorrectValue":
        //         errorMessage = "The value sent is incorrect.";
        //         break;
        //       case "InvalidTicket":
        //         errorMessage =
        //           "You have filled an invalid ticket. Please check your numbers and stars.";
        //         break;
        //       case "InvalidInputs":
        //         errorMessage = "The inputs sent are invalid.";
        //         break;
        //     }
        //     errorLog = revertError.message;
        //     errorMessage = error.shortMessage;
        //   } else {
        //     errorMessage = error.shortMessage;
        //     errorLog = error.message;
        //   }
        // } else {
        //   if (error instanceof Error) {
        //     errorMessage = error.message;
        //     errorLog = error.stack ?? error.name;
        //   } else if (typeof error == "string") {
        //     errorMessage = error;
        //   }
        // }

        const err = error as BaseError;

        handleError(err.shortMessage, "BuyTickets", String(err));
      }
    } finally {
      setBuying(false);
    }
  }

  /**
   * @description Withdraw prizes to the player's wallet.
   * @note Triggers when the user clicks the withdraw button.
   */
  async function withdrawPrizes(): Promise<void> {
    if (address == undefined) return;
    setWithdrawingPrizes(true);
    try {
      snackBar("Check your wallet to accept the transaction.", "ℹ️", undefined);

      try {
        await switchChain(config, { chainId: CHAIN.id });

        const { results } = await simulateCalls(config.getClient(), {
          account: address,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimPrizes",
            },
          ],
        });

        if (results[0].error) throw results[0].error;

        const { id } = await sendCalls(config, {
          account: address,
          chainId: CHAIN.id,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimPrizes",
            },
          ],
        });

        // snackBar("You will receive your prizes soon...", "⏳");

        await handleTransactionStatus(
          walletClient!,
          id,
          (hash: Address) => {
            SentryUtils.customIssue(`Prizes Withdrawn.`, "info");
            dismissSnackbar();
            snackBar(
              <div>
                <p>Transaction complete! Your prizes are now in your wallet.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(hash);
                  }}
                >
                  {hash.toString()}
                </p>
              </div>,
              "✅",
            );
            setUserInfo((userInfo) => userInfo?.updateToClaim(0n));
          },
          (failedHash: Address) => {
            handleError(
              `The transaction ${failedHash} failed.`,
              "WithdrawPrizes HandleTransactionStatus",
              undefined,
              <div>
                <p>We are very sorry. Your transaction failed.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(failedHash);
                  }}
                >
                  {failedHash.toString()}
                </p>
              </div>,
            );
          },
        );
      } catch (error) {
        const err = error as BaseError;

        handleError(err.shortMessage, "WithdrawPrizes", String(err));
      }
    } finally {
      setWithdrawingPrizes(false);
    }
  }

  /**
   * @description Withdraw donations to the charity's wallet.
   * @note Triggers when the charity clicks the withdraw button.
   */
  async function withdrawDonations(): Promise<void> {
    if (address == undefined) return;
    setWithdrawingDonations(true);
    try {
      snackBar("Check your wallet to accept the transaction.", "ℹ️", undefined);

      try {
        await switchChain(config, { chainId: CHAIN.id });

        const { results } = await simulateCalls(config.getClient(), {
          account: address,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimDonations",
            },
          ],
        });

        if (results[0].error) throw results[0].error;

        const { id } = await sendCalls(config, {
          account: address,
          chainId: CHAIN.id,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimDonations",
            },
          ],
        });

        // snackBar("You will receive your donations soon...", "⏳");

        await handleTransactionStatus(
          walletClient!,
          id,
          (hash: Address) => {
            SentryUtils.customIssue(`Donations Withdrawn.`, "info");
            dismissSnackbar();
            snackBar(
              <div>
                <p>
                  Transaction complete! Your donations are now in your wallet.
                </p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(hash);
                  }}
                >
                  {hash.toString()}
                </p>
              </div>,
              "✅",
            );
            setUserInfo((userInfo) => userInfo?.updateToClaim(undefined, 0n));
          },
          (failedHash: Address) => {
            handleError(
              `The transaction ${failedHash} failed.`,
              "WithdrawDonations HandleTransactionStatus",
              undefined,
              <div>
                <p>We are very sorry. Your transaction failed.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(failedHash);
                  }}
                >
                  {failedHash.toString()}
                </p>
              </div>,
            );
          },
        );
      } catch (error) {
        const err = error as BaseError;

        handleError(err.shortMessage, "WithdrawDonations", String(err));
      }
    } finally {
      setWithdrawingDonations(false);
    }
  }

  /**
   * @description Withdraw royalties to the owner's wallet.
   * @note Triggers when the owner clicks the withdraw button.
   */
  async function withdrawRoyalties(): Promise<void> {
    if (address == undefined) return;
    setWithdrawingRoyalties(true);
    try {
      snackBar("Check your wallet to accept the transaction.", "ℹ️", undefined);

      try {
        await switchChain(config, { chainId: CHAIN.id });

        const { results } = await simulateCalls(config.getClient(), {
          account: address,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimRoyalties",
            },
          ],
        });

        if (results[0].error) throw results[0].error;

        const { id } = await sendCalls(config, {
          account: address,
          chainId: CHAIN.id,
          calls: [
            {
              abi: bitMillionsAbi,
              to: bitMillionsAddress[CHAIN.id],
              functionName: "claimRoyalties",
            },
          ],
        });

        // snackBar("You will receive your donations soon...", "⏳");

        await handleTransactionStatus(
          walletClient!,
          id,
          (hash: Address) => {
            SentryUtils.customIssue(`Royalties Withdrawn.`, "info");
            dismissSnackbar();
            snackBar(
              <div>
                <p>
                  Transaction complete! Your royalties are now in your wallet.
                </p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(hash);
                  }}
                >
                  {hash.toString()}
                </p>
              </div>,
              "✅",
            );
            setUserInfo((userInfo) =>
              userInfo?.updateToClaim(undefined, undefined, 0n),
            );
          },
          (failedHash: Address) => {
            handleError(
              `The transaction ${failedHash} failed.`,
              "WithdrawRoyalties HandleTransactionStatus",
              undefined,
              <div>
                <p>We are very sorry. Your transaction failed.</p>
                <p
                  className="mt-1 cursor-pointer text-blue-500 underline whitespace-pre-wrap break-words break-all"
                  onClick={() => {
                    openTransactionUrl(failedHash);
                  }}
                >
                  {failedHash.toString()}
                </p>
              </div>,
            );
          },
        );
      } catch (error) {
        let errorMessage = "Something went wrong.";
        let errorLog = "";

        if (error instanceof BaseError) {
          errorMessage = error.shortMessage;
          errorLog = error.message;
        } else {
          if (error instanceof Error) {
            errorMessage = error.message;
            errorLog = error.stack ?? error.name;
          } else if (typeof error === "string") {
            errorMessage = error;
          }
        }
        handleError(errorMessage, "WithdrawRoyalties", errorLog);
      }
    } finally {
      setWithdrawingRoyalties(false);
    }
  }

  // Memoize context value for performance
  const contextValue = useMemo(
    () => ({
      isMiniApp,
      connectWallet,
      address,
      isConnecting,
      isConnected,
      config,
      buying,
      withdrawingPrizes,
      withdrawingDonations,
      withdrawingRoyalties,
      isLoading,
      animating,
      updatingDraws,
      drawInfo,
      drawDate,
      isOpen,
      statistics,
      drawsInHome,
      drawsInHistory,
      userInfo,
      ticketMappers,
      charities,
      fetchDrawsInPage,
      setTicketMappers,
      buyTickets,
      withdrawPrizes,
      withdrawDonations,
      withdrawRoyalties,
      playerStatistics,
    }),
    [
      isMiniApp,
      connectWallet,
      address,
      isConnecting,
      isConnected,
      config,
      buying,
      withdrawingPrizes,
      withdrawingDonations,
      withdrawingRoyalties,
      isLoading,
      animating,
      updatingDraws,
      drawInfo,
      drawDate,
      isOpen,
      statistics,
      drawsInHome,
      drawsInHistory,
      userInfo,
      ticketMappers,
      charities,
      fetchDrawsInPage,
      setTicketMappers,
      buyTickets,
      withdrawPrizes,
      withdrawDonations,
      withdrawRoyalties,
      playerStatistics,
    ],
  );
  return (
    <PlayerDataContext.Provider value={contextValue}>
      {children}
    </PlayerDataContext.Provider>
  );
};
